import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  buildDeviceMetricsViewModel,
  buildHubDeviceDrawerViewModel,
  type DeviceMetricsViewModel,
  type HubDeviceDrawerViewModel,
  type HubDeviceMarkerDatum
} from './deviceHealthProjection';
import {
  statusTelemetryClient,
  type DeviceMetricsResponse,
  type StatusTelemetryClient
} from './statusTelemetryApi';

export type HubDeviceDrawerController = {
  isOpen: boolean;
  view: 'hub' | 'device';
  hub: HubDeviceDrawerViewModel | null;
  selectedDeviceID: string | null;
  metrics: DeviceMetricsViewModel | null;
  loading: boolean;
  error: string | null;
  openHub: (datum: HubDeviceMarkerDatum, invoker?: HTMLElement | null) => void;
  selectDevice: (deviceID: string) => void;
  backToHub: () => void;
  retry: () => void;
  close: () => void;
};

export const useHubDeviceDrawerController = ({
  tenantID,
  data,
  client = statusTelemetryClient
}: {
  tenantID?: string;
  data: HubDeviceMarkerDatum[];
  client?: StatusTelemetryClient;
}): HubDeviceDrawerController => {
  const [selectedHubID, setSelectedHubID] = useState<string | null>(null);
  const datum = useMemo(
    () => data.find(candidate => candidate.id === selectedHubID) ?? null,
    [data, selectedHubID]
  );
  const [selectedDeviceID, setSelectedDeviceID] = useState<string | null>(null);
  const [response, setResponse] = useState<DeviceMetricsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const invokerRef = useRef<HTMLElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const close = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    const invoker = invokerRef.current;
    invokerRef.current = null;
    setSelectedHubID(null);
    setSelectedDeviceID(null);
    setResponse(null);
    setLoading(false);
    setError(null);
    window.requestAnimationFrame(() => invoker?.focus());
  }, []);

  const openHub = useCallback((nextDatum: HubDeviceMarkerDatum, invoker?: HTMLElement | null) => {
    abortRef.current?.abort();
    abortRef.current = null;
    invokerRef.current = invoker ?? null;
    setSelectedHubID(nextDatum.id);
    setSelectedDeviceID(null);
    setResponse(null);
    setLoading(false);
    setError(null);
  }, []);

  const selectDevice = useCallback((deviceID: string) => {
    setSelectedDeviceID(deviceID);
    setResponse(null);
    setError(null);
  }, []);

  const backToHub = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setSelectedDeviceID(null);
    setResponse(null);
    setLoading(false);
    setError(null);
  }, []);

  const retry = useCallback(() => setRetryToken(token => token + 1), []);

  useEffect(() => {
    if (!datum || !selectedDeviceID) return;
    const abort = new AbortController();
    abortRef.current?.abort();
    abortRef.current = abort;
    setLoading(true);
    setError(null);
    void client.fetchDeviceMetrics(selectedDeviceID, abort.signal)
      .then(next => {
        if (abort.signal.aborted) return;
        if (next.device.id !== selectedDeviceID || (tenantID && next.tenant_id !== tenantID)) {
          throw new Error('Device metrics response did not match the selected resource');
        }
        setResponse(next);
      })
      .catch(cause => {
        if (abort.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : 'Unable to load device metrics');
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [client, datum?.id, retryToken, selectedDeviceID, tenantID]);

  useEffect(() => close, [close, tenantID]);

  useEffect(() => {
    if (selectedHubID && !datum) close();
  }, [close, datum, selectedHubID]);

  useEffect(() => {
    if (
      selectedDeviceID &&
      datum &&
      !datum.devices.some(device => device.id === selectedDeviceID)
    ) {
      close();
    }
  }, [close, datum, selectedDeviceID]);

  return {
    isOpen: datum !== null,
    view: selectedDeviceID ? 'device' : 'hub',
    hub: useMemo(() => (datum ? buildHubDeviceDrawerViewModel(datum) : null), [datum]),
    selectedDeviceID,
    metrics: useMemo(() => (response ? buildDeviceMetricsViewModel(response) : null), [response]),
    loading,
    error,
    openHub,
    selectDevice,
    backToHub,
    retry,
    close
  };
};
